import {
  LogOut,
} from "lucide-react";
import {
  useClerk,
} from "@clerk/clerk-react";
import { ActiveOrg, User } from "@/schema";
import type React from "react";

import type { ChannelApi } from "../channel/useChannel";
import { ProfileManagementSection } from "../components/ProfileManagementSection";
import { activeContextStore } from "../services/ActiveContextStore";
import { authenticateService } from "../services/AuthenticateService";

interface UserProfileProps {
  user: User;
  activeOrg: ActiveOrg;
  channel: Pick<ChannelApi, "ready" | "query" | "hydrateArtifact">;
}

const formatOrgRole = (role?: string | null) => {
  if (!role) return "member";
  return String(role).replace(/^org:/, "").replace(/_/g, " ");
};

const UserProfile = ({ user, activeOrg, channel }: UserProfileProps) => {
  const clerk = useClerk();
  const activeUser = activeContextStore.read()?.user;

  const homeOrganizationName = activeOrg.name || activeOrg.id;
  const homeOrganizationRole = formatOrgRole(activeOrg.orgRole);
  const userEmail =
    activeUser?.email ||
    activeUser?.primaryEmail ||
    activeUser?.primaryEmailAddress ||
    "";
  const profileDisplayName =
    activeUser?.fullName?.trim() ||
    [activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(" ").trim() ||
    activeUser?.name?.trim() ||
    userEmail.split("@")[0] ||
    String(user.id);
  const initials = profileDisplayName.slice(0, 1).toUpperCase();

  const quotaResetDate = (() => {
    if (user?.subscriptionAnchor) {
      const today = new Date();
      const anchor = user.subscriptionAnchor;
      const month = today.getDate() >= anchor ? today.getMonth() + 1 : today.getMonth();
      return new Date(today.getFullYear(), month, anchor).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    }
    return new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  })();

  return (
    <div style={{ flex: 1, padding: "20px 16px 32px", display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Identity card */}
      <div style={card}>
        {/* Top row: avatar + name + actions */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          {/* Avatar */}
          <div style={avatar}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{initials}</span>
          </div>

          {/* Name + email */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {profileDisplayName}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {userEmail || "No email"}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => {
                activeContextStore.clear();
                authenticateService.clearAuth();
                void clerk.signOut();
              }}
              style={logoutBtn}
              title="Log out"
              type="button"
            >
              <LogOut size={13} />
              <span>Log out</span>
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "var(--color-border)", margin: "14px 0" }} />

        {/* Info grid */}
        <div style={infoGrid}>
          {user?.createdAt && (
            <>
              <span style={infoLabel}>Member since</span>
              <span style={infoValue}>{new Date(user.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            </>
          )}
          <span style={infoLabel}>{user?.subscriptionStatus === "auto_renewing" ? "Next payment" : "Quota resets"}</span>
          <span style={infoValue}>{quotaResetDate}</span>
          <span style={infoLabel}>Organization</span>
          <span style={infoValue}>{homeOrganizationName}</span>
          <span style={infoLabel}>Role</span>
          <span style={infoValue}>{homeOrganizationRole}</span>
        </div>
      </div>

      {/* Integrations / entities / members / organizations */}
      <ProfileManagementSection activeOrg={activeOrg} channel={channel} />

      {/* Brand + Contact + Legal */}
      <div style={card}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img src="/logo/causit.svg" alt="Causit" style={{ width: 22, height: 22 }} />
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--color-text-primary)" }}>
            caus<span style={{ color: "var(--accent-blue)" }}>it</span>
          </span>
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
          Every move forward is a proof backward — private business twins that coordinate on confidence, never guesswork.
        </div>

        {/* Contact */}
        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 12, marginBottom: 4 }}>
          <div style={sectionLabel}>Contact</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {[
              { name: "Ori Shmila", title: "Founder & CEO", email: "ori@causit.ai" },
              { name: "Ron Wislizki", title: "Co-Founder & COO", email: "ron@causit.ai" },
            ].map(({ name, title, email }) => (
              <div key={email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>{name}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{title}</div>
                </div>
                <a
                  href={`mailto:${email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: "var(--color-text-secondary)", textDecoration: "none", flexShrink: 0 }}
                  onMouseEnter={e => (e.currentTarget.style.color = "var(--color-text)")}
                  onMouseLeave={e => (e.currentTarget.style.color = "var(--color-text-secondary)")}
                >
                  {email}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Legal links — uncomment when ready
        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 12, marginTop: 12 }}>
          <div style={sectionLabel}>Legal</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 10 }}>
            {[
              { label: "Privacy Policy", href: "/terms/privacy" },
              { label: "Terms of Service", href: "/terms/service" },
              { label: "Cookie Policy", href: "/terms/cookies" },
              { label: "Return Policy", href: "/terms/return" },
              { label: "Security", href: "/terms/security" },
            ].map(({ label, href }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: "var(--color-text-secondary)", textDecoration: "none", padding: "3px 0" }}
                onMouseEnter={e => (e.currentTarget.style.color = "var(--color-text)")}
                onMouseLeave={e => (e.currentTarget.style.color = "var(--color-text-secondary)")}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
        */}
      </div>
    </div>
  );
};

const card: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  padding: "14px 14px 12px",
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--color-text-secondary)",
};

const avatar: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const logoutBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 28,
  padding: "0 10px",
  borderRadius: 7,
  border: "1px solid var(--color-border)",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-secondary)",
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const infoGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: 16,
  rowGap: 6,
  alignItems: "baseline",
};

const infoLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
};

const infoValue: React.CSSProperties = {
  fontSize: 13,
  color: "var(--color-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default UserProfile;
